import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { extractErrorMessage } from '../../core/services/api';
import {
  llmAgent,
  resources,
  type LlmJobState,
  type LlmJobStatus,
} from '../../core/services/resources';
import type { LineOfBiz } from '../../core/types/data-models';

/**
 * Shape of `result` returned by `generateIdustryAction`. Mirrors the
 * "Output format" section of the prompt template so the dialog can
 * render checkboxes per industry / segment without re-deriving the
 * shape at runtime.
 */
interface IdustryResult {
  introduction?: string;
  industry_identification?: Array<{
    industry: string;
    description?: string;
    segments?: Array<{ segment: string; description?: string }>;
  }>;
  conclusion?: string;
}

interface GenerateIdustriesDialogProps {
  open: boolean;
  lineOfBizId: number | null;
  onClose: () => void;
}

const TERMINAL_STATES: ReadonlySet<LlmJobState> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

/**
 * Dialog that drives the generate-idustry flow:
 *
 *   1. POST /lines-of-business/:id/generate-idustry to start a job.
 *   2. Poll GET /llm-agent/jobs/:jobId every 1.5s until terminal.
 *   3. Render the result with one checkbox per industry; the user
 *      picks which industries to save and clicks Save.
 *   4. PUT /lines-of-business/:id with `industries` set to the
 *      Markdown rendering of the selected industries.
 */
export function GenerateIdustriesDialog({
  open,
  lineOfBizId,
  onClose,
}: GenerateIdustriesDialogProps) {
  const queryClient = useQueryClient();

  // Job lifecycle state — all reset on every open. We keep the latest
  // status in a single object so the polling effect re-renders on each
  // tick without needing one state slot per field.
  const [status, setStatus] = useState<LlmJobStatus<IdustryResult> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // When false, the rendered Markdown omits segment descriptions —
  // user can opt to store only Industry / Segment names.
  const [includeDescriptions, setIncludeDescriptions] = useState(true);

  // Latest jobId — kept in a ref so the polling interval can read it
  // without re-subscribing every tick.
  const jobIdRef = useRef<string | null>(null);

  // ----- start job ----------------------------------------------------
  const start = useMutation({
    mutationFn: (id: number) => resources.generateLineOfBizIdustry(id),
    onSuccess: (data) => {
      jobIdRef.current = data.jobId;
      setStatus(data.status as LlmJobStatus<IdustryResult>);
    },
    onError: (err) => setStartError(extractErrorMessage(err)),
  });

  // ----- save selection ----------------------------------------------
  const save = useMutation({
    mutationFn: (markdown: string) =>
      resources.linesOfBusiness.update(lineOfBizId as number, {
        industries: markdown,
      } as Partial<LineOfBiz>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lines-of-business'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  // Restart the job from scratch — wipes prior status/selection so the
  // polling effect re-arms once the new start.mutate resolves. Used by
  // the Retry button when the job fails (e.g. transient 529 Overloaded).
  const retry = () => {
    if (lineOfBizId == null) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    start.mutate(lineOfBizId);
  };

  // ----- open / reset / start -----------------------------------------
  useEffect(() => {
    if (!open) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    if (lineOfBizId != null) start.mutate(lineOfBizId);
    // start.mutate identity is stable for the mutation hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lineOfBizId]);

  // ----- polling loop -------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (status == null) return;
    if (TERMINAL_STATES.has(status.state)) return;
    let cancelled = false;
    const tick = async () => {
      const jobId = jobIdRef.current;
      if (!jobId) return;
      try {
        const next = await llmAgent.getJob<IdustryResult>(jobId);
        if (!cancelled) setStatus(next);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    };
    const handle = window.setInterval(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, status]);

  // Default selection — once a job completes with industries, preselect
  // them all so Save is one click for the common "looks good" case.
  useEffect(() => {
    if (status?.state !== 'completed') return;
    const industries = status.result?.industry_identification ?? [];
    setSelected(new Set(industries.map((_, idx) => idx)));
  }, [status]);

  const industries = status?.result?.industry_identification ?? [];
  const lastStep = status?.steps?.[status.steps.length - 1]?.step;

  /** Render the selected industries as the Markdown shape the user spec'd. */
  const markdown = useMemo(() => {
    const picked = industries.filter((_, idx) => selected.has(idx));
    return picked
      .map((ind) => {
        const segs = ind.segments ?? [];
        const segLines = segs
          .map((s) => {
            const desc = includeDescriptions && s.description ? ` - ${s.description}` : '';
            return ` - ${s.segment}${desc}`;
          })
          .join('\n');
        return `## ${ind.industry}${segLines ? `\n${segLines}` : ''}`;
      })
      .join('\n');
  }, [industries, selected, includeDescriptions]);

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const isCompleted = status?.state === 'completed';
  const isRunning =
    start.isPending ||
    (status != null && !TERMINAL_STATES.has(status.state));
  const canSave = isCompleted && selected.size > 0 && !save.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setError(null);
    await save.mutateAsync(markdown);
  };

  return (
    <FormDialog
      open={open}
      title="Analyze and define Industries"
      submitLabel={save.isPending ? 'Saving…' : 'Save selected'}
      loading={save.isPending}
      submitDisabled={!canSave}
      maxWidth="lg"
      onSubmit={handleSubmit}
      onClose={() => {
        if (save.isPending) return;
        onClose();
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {/* ---- Job progress ---- */}
      {isRunning && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {status?.state === 'pending' || status == null
                ? 'Starting…'
                : lastStep ?? 'Analyzing industries…'}
            </Typography>
          </Stack>
          <LinearProgress
            variant={
              status?.progress != null && status.progress > 0
                ? 'determinate'
                : 'indeterminate'
            }
            value={
              status?.progress != null
                ? Math.round(status.progress * 100)
                : undefined
            }
          />
        </Stack>
      )}

      {status?.state === 'failed' && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {status.error || 'The industry analysis job failed.'}
        </Alert>
      )}
      {status?.state === 'cancelled' && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          The industry analysis job was cancelled.
        </Alert>
      )}
      {startError && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {startError}
        </Alert>
      )}

      {/* ---- Results review ---- */}
      {isCompleted && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {status?.result?.introduction && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Introduction</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  {status.result.introduction}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {industries.length === 0 ? (
            <Alert severity="warning">
              The analyzer returned no industries.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {industries.map((ind, idx) => (
                <Paper
                  key={`${ind.industry}-${idx}`}
                  variant="outlined"
                  sx={{ p: 1.5 }}
                >
                  {/* Checkbox + title share one row (vertically
                      centered against the title). When the model also
                      returns an industry-level description we render it
                      below the title, indented to line up with the
                      title text — keeps the click target tight to the
                      heading. */}
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Checkbox
                      checked={selected.has(idx)}
                      onChange={() => toggle(idx)}
                      sx={{ p: 0.5 }}
                    />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {ind.industry}
                    </Typography>
                  </Stack>
                  {ind.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ pl: 4.25 }}
                    >
                      {ind.description}
                    </Typography>
                  )}
                  {(ind.segments?.length ?? 0) > 0 && (
                    <Stack spacing={0.25} sx={{ mt: 1, pl: 4 }}>
                      {ind.segments?.map((seg, sIdx) => (
                        <Typography
                          key={`${seg.segment}-${sIdx}`}
                          variant="body2"
                        >
                          <strong>{seg.segment}</strong>
                          {seg.description ? ` — ${seg.description}` : ''}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Paper>
              ))}
            </Stack>
          )}

          {status?.result?.conclusion && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Conclusion</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">{status.result.conclusion}</Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Live preview of the Markdown that will be written to
              `LineOfBiz.industries`. Helps the user understand what
              they're saving before they commit. The include-descriptions
              switch lets the user store just Industry / Segment names. */}
          {selected.size > 0 && (
            <Box>
              <Divider sx={{ mb: 1.5 }} />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">Preview (Markdown)</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={includeDescriptions}
                      onChange={(e) => setIncludeDescriptions(e.target.checked)}
                    />
                  }
                  label="Include segment descriptions"
                  labelPlacement="start"
                  sx={{ m: 0 }}
                />
              </Stack>
              <Paper
                variant="outlined"
                sx={{ p: 1.5, backgroundColor: 'background.default' }}
              >
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    fontFamily: 'monospace',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {markdown}
                </Box>
              </Paper>
            </Box>
          )}
        </Stack>
      )}
    </FormDialog>
  );
}
